import { useState } from 'react'
import { Plus, GripVertical } from 'lucide-react'
import { useAppEvent } from '../../AppContext.tsx'
import { rule as makeRule, type Rule } from 'graph-grammar'
import { RuleRow } from './RuleRow.tsx'
import { CollapsibleGroup } from '../Collapsible.tsx'

/** Move `drag` to just before `target` in `keys`, returning the new order. */
function reorder(keys: string[], drag: string, target: string): string[] {
  if (drag === target) return keys
  const next = keys.filter((k) => k !== drag)
  const at = next.indexOf(target)
  if (at < 0) return keys
  next.splice(at, 0, drag)
  return next
}

const CAP = 200
const UNGROUPED = 'Ungrouped'

/** Sidebar list of rules with live match-count badges, reordering, and
 *  collapsible rule groups (by each rule's optional `group`). */
export function RuleList() {
  const app = useAppEvent('rules', 'selectRule', 'graph')
  const counts = app.engine.matchCounts(CAP)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [dragGroup, setDragGroup] = useState<string | null>(null)
  const [overGroup, setOverGroup] = useState<string | null>(null)
  const [dragRule, setDragRule] = useState<string | null>(null)
  const [overRule, setOverRule] = useState<string | null>(null)

  const dropRule = (targetId: string) => {
    if (dragRule && dragRule !== targetId) app.reorderRules(dragRule, targetId)
    setDragRule(null)
    setOverRule(null)
  }

  const newRule = () => {
    app.addRule(
      makeRule({
        name: `Rule ${app.grammar.rules.length + 1}`,
        lhs: { nodes: [], edges: [] },
        rhs: { nodes: [], edges: [] },
      }),
    )
  }

  const renderRow = (r: Rule) => {
    const count = counts[r.id] ?? 0
    return (
      <RuleRow
        key={r.id}
        rule={r}
        count={count}
        cap={CAP}
        blocked={count > 0 && app.engine.isBlockedByNodeCap(r)}
        active={r.id === app.activeRuleId}
        dnd={{
          dragging: dragRule === r.id,
          over: overRule === r.id,
          onStart: () => setDragRule(r.id),
          onOver: () => setOverRule((o) => (o === r.id ? o : r.id)),
          onLeave: () => setOverRule((o) => (o === r.id ? null : o)),
          onDrop: () => dropRule(r.id),
          onEnd: () => {
            setDragRule(null)
            setOverRule(null)
          },
        }}
      />
    )
  }

  const rules = app.grammar.rules
  const hasGroups = rules.some((r) => r.group)

  // Group rules by their `group`, preserving first-seen order. Ungrouped rules
  // collect under a trailing "Ungrouped" section (only shown when some rules are
  // grouped , otherwise the list renders flat, exactly as before).
  const order: string[] = []
  const byGroup = new Map<string, Rule[]>()
  for (const r of rules) {
    const g = r.group || UNGROUPED
    if (!byGroup.has(g)) {
      byGroup.set(g, [])
      order.push(g)
    }
    byGroup.get(g)!.push(r)
  }

  const toggle = (g: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      next.has(g) ? next.delete(g) : next.add(g)
      return next
    })

  const setGroupEnabled = (group: Rule[], enabled: boolean) => {
    for (const r of group) r.enabled = enabled
    app.touchRules()
  }

  // Drag-to-reorder whole group sections. `order` holds display keys (UNGROUPED
  // for the ungrouped bucket); map that sentinel back to null for the state API.
  const dropGroup = (target: string) => {
    if (dragGroup && dragGroup !== target) {
      const next = reorder(order, dragGroup, target)
      app.reorderRuleGroups(next.map((g) => (g === UNGROUPED ? null : g)))
    }
    setDragGroup(null)
    setOverGroup(null)
  }

  return (
    <div className="rule-list">
      <div className="panel-head">
        <h3>Rules</h3>
        <button className="primary small" onClick={newRule}>
          <Plus size={14} /> New rule
        </button>
      </div>
      <div className="rules">
        {!hasGroups && rules.map(renderRow)}
        {hasGroups &&
          order.map((g) => {
            const group = byGroup.get(g)!
            const allOn = group.every((r) => r.enabled)
            const someOn = group.some((r) => r.enabled)
            // Aggregate live matches across the group's rules (not just the rule
            // count), so a collapsed group still shows how "hot" it is. Per-rule
            // counts are capped at CAP, so flag the sum with "+" if any maxed out.
            const groupMatches = group.reduce((s, r) => s + (counts[r.id] ?? 0), 0)
            const groupCapped = group.some((r) => (counts[r.id] ?? 0) >= CAP)
            const groupBadge = groupCapped ? `${groupMatches}+` : String(groupMatches)
            return (
              <CollapsibleGroup
                key={g}
                title={g}
                collapsed={collapsed.has(g)}
                onToggle={() => toggle(g)}
                className={(dragGroup === g ? 'dragging' : '') + (overGroup === g ? ' drag-over' : '')}
                handle={
                  <span className="drag-handle" title="Drag to reorder group" aria-hidden>
                    <GripVertical size={14} />
                  </span>
                }
                headProps={{
                  draggable: true,
                  onDragStart: (e) => {
                    setDragGroup(g)
                    e.dataTransfer.effectAllowed = 'move'
                  },
                  onDragOver: (e) => {
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                    if (overGroup !== g) setOverGroup(g)
                  },
                  onDragLeave: () => setOverGroup((k) => (k === g ? null : k)),
                  onDrop: (e) => {
                    e.preventDefault()
                    dropGroup(g)
                  },
                  onDragEnd: () => {
                    setDragGroup(null)
                    setOverGroup(null)
                  },
                }}
                right={
                  <>
                    <span
                      className={'match-badge' + (groupMatches > 0 ? ' has' : '')}
                      title={`${groupMatches} current matches across this group`}
                    >
                      {groupBadge}
                    </span>
                    <span className="group-count" title={`${group.length} rules`}>
                      {group.length}
                    </span>
                    <input
                      type="checkbox"
                      className="group-enable"
                      title={allOn ? 'Disable all in group' : 'Enable all in group'}
                      checked={allOn}
                      ref={(el) => {
                        if (el) el.indeterminate = someOn && !allOn
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setGroupEnabled(group, e.target.checked)}
                    />
                  </>
                }
              >
                <div className="rules">{group.map(renderRow)}</div>
              </CollapsibleGroup>
            )
          })}
        {rules.length === 0 && <div className="empty-hint">No rules yet , create one to begin.</div>}
      </div>
    </div>
  )
}
